// src/components/profile/PreferencesSettings.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Divider,
  Card,
  CardContent,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Switch,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Button,
  Alert,
  Select,
  MenuItem,
  InputLabel,
  Grid,
  Chip
} from '@mui/material';
import {
  Palette as PaletteIcon,
  Language as LanguageIcon,
  Notifications as NotificationsIcon,
  ViewModule as ViewModuleIcon,
  Speed as SpeedIcon,
  Accessibility as AccessibilityIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  SettingsBrightness as AutoModeIcon,
  Save as SaveIcon,
  RestoreFromTrash as RestoreIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';

const PreferencesSettings = () => {
  const { user, updateProfile } = useAuth();
  const [preferences, setPreferences] = useState({
    theme: 'system', // light, dark, system
    language: 'es', // es, en
    density: 'comfortable', // compact, comfortable, spacious
    animations: true,
    soundEffects: false,
    highContrast: false,
    fontSize: 'medium', // small, medium, large
    autoSave: true,
    compactSidebar: false,
    showTooltips: true,
    dateFormat: 'DD/MM/YYYY',
    timeFormat: '24h'
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Cargar preferencias del usuario al inicializar
  useEffect(() => {
    if (user?.preferences) {
      setPreferences(prev => ({
        ...prev,
        ...user.preferences
      }));
    }
  }, [user]);

  const handlePreferenceChange = (key, value) => {
    setPreferences(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSwitchChange = (key) => (event) => {
    handlePreferenceChange(key, event.target.checked);
  };

  const handleRadioChange = (key) => (event) => {
    handlePreferenceChange(key, event.target.value);
  };

  const handleSelectChange = (key) => (event) => {
    handlePreferenceChange(key, event.target.value);
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);

    try {
      await updateProfile({ preferences });
      
      // Aplicar tema inmediatamente
      applyTheme(preferences.theme);
      
      setMessage({ 
        type: 'success', 
        text: 'Preferencias guardadas correctamente' 
      });

      // Limpiar mensaje después de 3 segundos
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: 'Error al guardar las preferencias' 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    const defaultPreferences = {
      theme: 'system',
      language: 'es',
      density: 'comfortable',
      animations: true,
      soundEffects: false,
      highContrast: false,
      fontSize: 'medium',
      autoSave: true,
      compactSidebar: false,
      showTooltips: true,
      dateFormat: 'DD/MM/YYYY',
      timeFormat: '24h'
    };
    setPreferences(defaultPreferences);
  };

  const applyTheme = (theme) => {
    const root = document.documentElement;
    
    if (theme === 'dark') {
      root.classList.add('dark-theme');
      root.classList.remove('light-theme');
    } else if (theme === 'light') {
      root.classList.add('light-theme');
      root.classList.remove('dark-theme');
    } else {
      // System theme
      root.classList.remove('dark-theme', 'light-theme');
      if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
        root.classList.add('dark-theme');
      }
    }
  };

  const getThemeIcon = (theme) => {
    switch (theme) {
      case 'light': return <LightModeIcon />;
      case 'dark': return <DarkModeIcon />;
      default: return <AutoModeIcon />;
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom fontWeight="bold">
        Preferencias
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Personaliza la apariencia y comportamiento de la aplicación
      </Typography>

      <Divider sx={{ mb: 3 }} />

      {/* Mensaje de estado */}
      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }}>
          {message.text}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Apariencia */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <PaletteIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Apariencia
                </Typography>
              </Box>

              {/* Tema */}
              <FormControl component="fieldset" sx={{ mb: 3 }}>
                <FormLabel component="legend">Tema</FormLabel>
                <RadioGroup
                  value={preferences.theme}
                  onChange={handleRadioChange('theme')}
                >
                  <FormControlLabel
                    value="light"
                    control={<Radio />}
                    label={
                      <Box display="flex" alignItems="center">
                        <LightModeIcon sx={{ mr: 1 }} />
                        Claro
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="dark"
                    control={<Radio />}
                    label={
                      <Box display="flex" alignItems="center">
                        <DarkModeIcon sx={{ mr: 1 }} />
                        Oscuro
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="system"
                    control={<Radio />}
                    label={
                      <Box display="flex" alignItems="center">
                        <AutoModeIcon sx={{ mr: 1 }} />
                        Automático (sistema)
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>

              {/* Tamaño de fuente */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Tamaño de fuente</InputLabel>
                <Select
                  value={preferences.fontSize}
                  label="Tamaño de fuente"
                  onChange={handleSelectChange('fontSize')}
                >
                  <MenuItem value="small">Pequeño</MenuItem>
                  <MenuItem value="medium">Mediano</MenuItem>
                  <MenuItem value="large">Grande</MenuItem>
                </Select>
              </FormControl>

              {/* Densidad */}
              <FormControl fullWidth>
                <InputLabel>Densidad de la interfaz</InputLabel>
                <Select
                  value={preferences.density}
                  label="Densidad de la interfaz"
                  onChange={handleSelectChange('density')}
                >
                  <MenuItem value="compact">Compacta</MenuItem>
                  <MenuItem value="comfortable">Cómoda</MenuItem>
                  <MenuItem value="spacious">Espaciosa</MenuItem>
                </Select>
              </FormControl>
            </CardContent>
          </Card>
        </Grid>

        {/* Comportamiento */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <ViewModuleIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Comportamiento
                </Typography>
              </Box>

              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <SpeedIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Animaciones"
                    secondary="Activar animaciones de la interfaz"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={preferences.animations}
                      onChange={handleSwitchChange('animations')}
                      color="primary"
                    />
                  </ListItemSecondaryAction>
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <NotificationsIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Efectos de sonido"
                    secondary="Reproducir sonidos en notificaciones"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={preferences.soundEffects}
                      onChange={handleSwitchChange('soundEffects')}
                      color="primary"
                    />
                  </ListItemSecondaryAction>
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <SaveIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Guardado automático"
                    secondary="Guardar cambios automáticamente"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={preferences.autoSave}
                      onChange={handleSwitchChange('autoSave')}
                      color="primary"
                    />
                  </ListItemSecondaryAction>
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <ViewModuleIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Sidebar compacto"
                    secondary="Mostrar sidebar en modo compacto"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={preferences.compactSidebar}
                      onChange={handleSwitchChange('compactSidebar')}
                      color="primary"
                    />
                  </ListItemSecondaryAction>
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <AccessibilityIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Mostrar tooltips"
                    secondary="Mostrar ayuda contextual"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={preferences.showTooltips}
                      onChange={handleSwitchChange('showTooltips')}
                      color="primary"
                    />
                  </ListItemSecondaryAction>
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <AccessibilityIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Alto contraste"
                    secondary="Mejorar contraste para accesibilidad"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={preferences.highContrast}
                      onChange={handleSwitchChange('highContrast')}
                      color="primary"
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Localización */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <LanguageIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Localización
                </Typography>
              </Box>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Idioma</InputLabel>
                <Select
                  value={preferences.language}
                  label="Idioma"
                  onChange={handleSelectChange('language')}
                >
                  <MenuItem value="es">Español</MenuItem>
                  <MenuItem value="en">English</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Formato de fecha</InputLabel>
                <Select
                  value={preferences.dateFormat}
                  label="Formato de fecha"
                  onChange={handleSelectChange('dateFormat')}
                >
                  <MenuItem value="DD/MM/YYYY">DD/MM/YYYY</MenuItem>
                  <MenuItem value="MM/DD/YYYY">MM/DD/YYYY</MenuItem>
                  <MenuItem value="YYYY-MM-DD">YYYY-MM-DD</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Formato de hora</InputLabel>
                <Select
                  value={preferences.timeFormat}
                  label="Formato de hora"
                  onChange={handleSelectChange('timeFormat')}
                >
                  <MenuItem value="12h">12 horas (AM/PM)</MenuItem>
                  <MenuItem value="24h">24 horas</MenuItem>
                </Select>
              </FormControl>
            </CardContent>
          </Card>
        </Grid>

        {/* Vista previa del tema actual */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Vista Previa
              </Typography>
              <Box display="flex" alignItems="center" mb={2}>
                {getThemeIcon(preferences.theme)}
                <Typography variant="body1" sx={{ ml: 1 }}>
                  Tema: {preferences.theme === 'light' ? 'Claro' : preferences.theme === 'dark' ? 'Oscuro' : 'Automático'}
                </Typography>
              </Box>
              <Box display="flex" gap={1} flexWrap="wrap">
                <Chip label={`Fuente: ${preferences.fontSize}`} size="small" />
                <Chip label={`Densidad: ${preferences.density}`} size="small" />
                <Chip label={`Idioma: ${preferences.language === 'es' ? 'Español' : 'English'}`} size="small" />
                {preferences.animations && <Chip label="Animaciones" size="small" color="primary" />}
                {preferences.highContrast && <Chip label="Alto contraste" size="small" color="secondary" />}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Botones de acción */}
      <Box display="flex" justifyContent="space-between" mt={3}>
        <Button
          variant="outlined"
          onClick={handleReset}
          startIcon={<RestoreIcon />}
        >
          Restaurar por defecto
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={<SaveIcon />}
        >
          {saving ? 'Guardando...' : 'Guardar Preferencias'}
        </Button>
      </Box>
    </Box>
  );
};

export default PreferencesSettings;